import type { DefLookup } from "./catalog";
import { dcBreakerSpec } from "./dc-breaker";
import { buildGraph, v } from "./diagnostics/graph";
import type { System } from "./types";

/** Conductors on each side of the physical breaker must carry its rating.
 * Walk copper until another protective boundary. Do not cross battery cells,
 * loads or converters, and never turn a protective rating into load current.
 * This ampacity floor does not certify upstream fault protection/placement. */
export function dcBreakerConductorRatings(system: System, lookup: DefLookup): Map<string, { ratingA: number; nodeIds: string[] }> {
  const breakers = system.nodes.flatMap(node => {
    if (node.kind !== "component") return [];
    const def = lookup(node.definitionId, node.customDefinition);
    const spec = def && dcBreakerSpec(def);
    return spec ? [{ node, spec }] : [];
  });
  const result = new Map<string, { ratingA: number; nodeIds: string[] }>();
  if (!breakers.length) return result;
  const graph = buildGraph(system, lookup, { excludeFused: true, excludeSense: true });
  for (const { node, spec } of breakers) {
    const queue = [v(node.id, spec.inputTerminalId), v(node.id, spec.outputTerminalId)];
    const seen = new Set(queue);
    for (let i = 0; i < queue.length; i++) {
      for (const { vertex, via } of graph.adj.get(queue[i]) ?? []) {
        if (via.kind === "cable") {
          const previous = result.get(via.cableId);
          result.set(via.cableId, { ratingA: Math.max(previous?.ratingA ?? 0, spec.ratingA),
            nodeIds: [...new Set([...(previous?.nodeIds ?? []), node.id])] });
        }
        if (!seen.has(vertex)) { seen.add(vertex); queue.push(vertex); }
      }
    }
  }
  return result;
}
